import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Swal from "sweetalert2";
import axios from "../../../utils/axiosConfig";
import "../../../Pages/Css/dianDocumentsHub.css";

const getDocumentArray = (payload) => {
  if (Array.isArray(payload)) return payload;
  const candidates = [
    payload?.data,
    payload?.documents,
    payload?.documentos,
    payload?.result,
    payload?.rows,
  ];
  return candidates.find(Array.isArray) || [];
};

const formatBytes = (bytes) => {
  const value = Number(bytes);
  if (!Number.isFinite(value) || value <= 0) return "Tamaño no disponible";
  const units = ["B", "KB", "MB", "GB"];
  let size = value;
  let index = 0;
  while (size >= 1024 && index < units.length - 1) {
    size /= 1024;
    index += 1;
  }
  return `${size.toFixed(size >= 10 || index === 0 ? 0 : 1)} ${units[index]}`;
};

const resolveDocument = (document, index) => ({
  id: document?.ID ?? document?.Id ?? document?.id ?? `${document?.NombreArchivo || "pdf"}-${index}`,
  name:
    document?.NombreArchivo ??
    document?.FileName ??
    document?.filename ??
    document?.name ??
    `Documento DIAN ${index + 1}.pdf`,
  url:
    document?.Url ??
    document?.URL ??
    document?.FileUrl ??
    document?.Ruta ??
    document?.url ??
    "",
  size: document?.Size ?? document?.Tamano ?? document?.size,
  uploadedAt:
    document?.FechaCreacion ??
    document?.CreatedAt ??
    document?.Fecha ??
    document?.uploadedAt ??
    "",
});

const DianDocumentsHub = ({ contrato }) => {
  const contractId = contrato?.ContractID ?? contrato ?? null;
  const inputRef = useRef(null);
  const [documents, setDocuments] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${localStorage.getItem("tkn") || ""}` }),
    []
  );

  const loadDocuments = useCallback(async () => {
    if (!contractId || contractId === -1 || contractId === "-1" || contractId === "ND") return;
    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER}/dian/getdocumentos`,
        { contrato: contractId },
        { headers: authHeaders }
      );
      setDocuments(getDocumentArray(response?.data).map(resolveDocument));
    } catch (error) {
      setDocuments([]);
      console.error("No fue posible consultar los documentos DIAN:", error);
    } finally {
      setLoading(false);
    }
  }, [authHeaders, contractId]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  useEffect(() => {
    if (!selectedFile) return undefined;
    const objectUrl = URL.createObjectURL(selectedFile);
    setPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [selectedFile]);

  const selectPdf = (file) => {
    if (!file) return;
    if (file.type !== "application/pdf" && !file.name?.toLowerCase().endsWith(".pdf")) {
      Swal.fire("Archivo no válido", "Selecciona únicamente archivos PDF.", "warning");
      return;
    }
    setSelectedFile(file);
  };

  const uploadDocument = async () => {
    if (!selectedFile || !contractId || uploading) return;
    const formData = new FormData();
    formData.append("contrato", contractId);
    formData.append("file", selectedFile);
    setUploading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER}/dian/uploaddocumento`,
        formData,
        {
          headers: authHeaders,
          transformRequest: [(data) => data],
        }
      );
      if (response?.data?.success === false) {
        throw new Error(response.data.message || "La API no confirmó la subida.");
      }
      await loadDocuments();
      Swal.fire("Documento cargado", "El PDF quedó asociado al contrato.", "success");
    } catch (error) {
      Swal.fire(
        "No fue posible subir el PDF",
        error?.response?.data?.message || error?.message || "Verifica el servicio de documentos DIAN.",
        "error"
      );
    } finally {
      setUploading(false);
    }
  };

  const openDocumentPreview = (document) => {
    if (!document.url) {
      Swal.fire("Vista previa no disponible", "El documento no incluye una URL de consulta.", "info");
      return;
    }
    setSelectedFile(null);
    setPreviewUrl(document.url);
  };

  return (
    <div className="dian-documents-hub">
      <header className="dian-documents-header">
        <div>
          <span className="dian-documents-eyebrow">Gestión documental</span>
          <h5>Documentos DIAN</h5>
          <p>Sube, consulta y previsualiza los documentos PDF asociados al contrato.</p>
        </div>
        <div className="dian-documents-header-actions">
          <span className="dian-documents-contract">Contrato: {contractId || "ND"}</span>
          <button type="button" className="dian-documents-close" data-bs-dismiss="modal" aria-label="Cerrar">
            <i className="bx bx-x" aria-hidden="true"></i>
          </button>
        </div>
      </header>

      <div className="dian-documents-layout">
        <section className="dian-documents-upload-panel">
          <div className="dian-documents-section-title">
            <i className="bx bx-upload" aria-hidden="true"></i>
            <div><strong>Subir archivo PDF</strong><span>Máximo un archivo por carga.</span></div>
          </div>
          <button
            type="button"
            className={`dian-dropzone ${dragging ? "is-dragging" : ""}`}
            onClick={() => inputRef.current?.click()}
            onDragEnter={(event) => { event.preventDefault(); setDragging(true); }}
            onDragOver={(event) => event.preventDefault()}
            onDragLeave={(event) => { event.preventDefault(); setDragging(false); }}
            onDrop={(event) => {
              event.preventDefault();
              setDragging(false);
              selectPdf(event.dataTransfer.files?.[0]);
            }}
          >
            <i className="bx bxs-cloud-upload" aria-hidden="true"></i>
            <strong>{selectedFile ? selectedFile.name : "Seleccionar o arrastrar PDF"}</strong>
            <span>{selectedFile ? formatBytes(selectedFile.size) : "Solo archivos con extensión .pdf"}</span>
          </button>
          <input ref={inputRef} hidden type="file" accept="application/pdf,.pdf" onChange={(event) => selectPdf(event.target.files?.[0])} />
          <div className="dian-documents-upload-actions">
            {selectedFile && <button type="button" className="dian-btn dian-btn-secondary" onClick={() => { setSelectedFile(null); setPreviewUrl(""); }}>Quitar</button>}
            <button type="button" className="dian-btn dian-btn-primary" disabled={!selectedFile || uploading} onClick={uploadDocument}>
              <i className={`bx ${uploading ? "bx-loader-alt bx-spin" : "bx-upload"}`} aria-hidden="true"></i>
              {uploading ? "Subiendo..." : "Subir documento"}
            </button>
          </div>
        </section>

        <section className="dian-documents-preview-panel">
          <div className="dian-documents-section-title">
            <i className="bx bx-show" aria-hidden="true"></i>
            <div><strong>Previsualización</strong><span>Vista integrada del documento seleccionado.</span></div>
          </div>
          {previewUrl ? (
            <iframe title="Previsualización del documento DIAN" src={previewUrl} className="dian-pdf-preview" />
          ) : (
            <div className="dian-preview-empty"><i className="bx bxs-file-pdf"></i><span>Selecciona un PDF para previsualizarlo.</span></div>
          )}
        </section>
      </div>

      <section className="dian-documents-list">
        <div className="dian-documents-list-header">
          <div><h6>Archivos cargados</h6><span>{documents.length} documento(s)</span></div>
          <button type="button" className="dian-refresh-btn" onClick={loadDocuments} disabled={loading} title="Actualizar listado">
            <i className={`bx bx-refresh ${loading ? "bx-spin" : ""}`}></i>
          </button>
        </div>
        {loading ? (
          <div className="dian-documents-empty">Consultando documentos...</div>
        ) : documents.length ? (
          <div className="dian-documents-grid">
            {documents.map((document) => (
              <button type="button" className="dian-document-card" key={document.id} onClick={() => openDocumentPreview(document)}>
                <span className="dian-pdf-icon"><i className="bx bxs-file-pdf"></i></span>
                <span className="dian-document-copy">
                  <strong title={document.name}>{document.name}</strong>
                  <small>{formatBytes(document.size)}</small>
                  {document.uploadedAt && <small>{document.uploadedAt}</small>}
                </span>
                <i className="bx bx-show dian-document-view" aria-hidden="true"></i>
              </button>
            ))}
          </div>
        ) : (
          <div className="dian-documents-empty"><i className="bx bx-folder-open"></i><span>Aún no hay documentos cargados.</span></div>
        )}
      </section>
    </div>
  );
};

export default DianDocumentsHub;
